import { isTiptapJsonDoc, type TiptapDocJSON } from '../../utils/contentUtils'

export interface DocumentTemplate {
  id: string
  name: string
  description: string
  icon: string | null
  color: string | null
  content: TiptapDocJSON
  includeInExport: boolean
  includeInWordCount: boolean
  preset: boolean
}

function labelParagraph(label: string) {
  return {
    type: 'paragraph',
    content: [{ type: 'text', marks: [{ type: 'bold' }], text: `${label}:` }],
  }
}

function labelList(labels: string[]): TiptapDocJSON {
  return {
    type: 'doc',
    content: [{
      type: 'bulletList',
      content: labels.map(label => ({
        type: 'listItem',
        content: [labelParagraph(label)],
      })),
    }],
  }
}

export const defaultDocumentTemplates: DocumentTemplate[] = [
  {
    id: 'preset-character',
    name: 'Character',
    description: 'A flexible profile for fictional or biographical subjects.',
    icon: 'document:bookmark-3-line',
    color: '#b96072',
    content: labelList(['Role', 'Goal', 'Motivation', 'Conflict', 'Appearance', 'Background']),
    includeInExport: false,
    includeInWordCount: false,
    preset: true,
  },
  {
    id: 'preset-setting',
    name: 'Setting',
    description: 'Places, environments, and worldbuilding details.',
    icon: 'document:map-pin-add-line',
    color: '#4f8a73',
    content: labelList(['Type', 'Region', 'Sensory details', 'History', 'Story significance', 'Continuity notes']),
    includeInExport: false,
    includeInWordCount: false,
    preset: true,
  },
  {
    id: 'preset-scene-plan',
    name: 'Scene Plan',
    description: 'A compact planning sheet for a scene or sequence.',
    icon: 'document:draft-line',
    color: '#8a6bb3',
    content: labelList(['Point of view', 'Setting and time', 'Scene goal', 'Conflict or turn', 'Outcome', 'Setup and payoff']),
    includeInExport: false,
    includeInWordCount: false,
    preset: true,
  },
  {
    id: 'preset-research-source',
    name: 'Research Source',
    description: 'Capture a source, its claims, and where it supports the manuscript.',
    icon: 'document:article-line',
    color: '#497fa8',
    content: labelList(['Source or citation', 'Link', 'Date accessed', 'Summary', 'Key facts', 'Quotations to verify', 'Where used']),
    includeInExport: false,
    includeInWordCount: false,
    preset: true,
  },
  {
    id: 'preset-chapter-outline',
    name: 'Chapter Outline',
    description: 'A lightweight structure for fiction, memoir, or nonfiction chapters.',
    icon: 'document:file-list-3-line',
    color: '#a47742',
    content: labelList(['Purpose', 'Opening state', 'Major beats', 'Closing state', 'Questions to resolve']),
    includeInExport: false,
    includeInWordCount: false,
    preset: true,
  },
]

function cloneTemplateData<T>(value: T): T {
  // Template content is JSON data, but the template editor exposes it through
  // Vue's reactive proxies. structuredClone rejects Proxy objects, which made
  // both the dialog's Close and Use actions throw before they could emit.
  return JSON.parse(JSON.stringify(value)) as T
}

export function cloneDocumentTemplate(template: DocumentTemplate): DocumentTemplate {
  return cloneTemplateData(template)
}

export function normalizeDocumentTemplate(value: any): DocumentTemplate | null {
  if (!value || typeof value !== 'object' || typeof value.id !== 'string') return null
  const content = isTiptapJsonDoc(value.content)
    ? cloneTemplateData(value.content)
    : { type: 'doc', content: [{ type: 'paragraph' }] } as TiptapDocJSON
  return {
    id: value.id.slice(0, 100),
    name: typeof value.name === 'string' && value.name.trim() ? value.name.trim().slice(0, 100) : 'Untitled Template',
    description: typeof value.description === 'string' ? value.description.trim().slice(0, 280) : '',
    icon: typeof value.icon === 'string' ? value.icon : null,
    color: typeof value.color === 'string' ? value.color : null,
    content,
    includeInExport: value.includeInExport === true,
    includeInWordCount: value.includeInWordCount === true,
    preset: value.preset === true,
  }
}

export function normalizeDocumentTemplates(value: unknown): DocumentTemplate[] {
  if (!Array.isArray(value)) return cloneTemplateData(defaultDocumentTemplates)
  const seen = new Set<string>()
  return value
    .map(normalizeDocumentTemplate)
    .filter((template): template is DocumentTemplate => {
      if (!template || seen.has(template.id)) return false
      seen.add(template.id)
      return true
    })
    .slice(0, 100)
}

export function createBlankDocumentTemplate(): DocumentTemplate {
  return {
    id: `template_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    name: 'New Template',
    description: '',
    icon: 'document:sticky-note-line',
    color: null,
    content: { type: 'doc', content: [{ type: 'paragraph' }] },
    includeInExport: false,
    includeInWordCount: false,
    preset: false,
  }
}
